/** The payoff outline on a strategy card.
 *
 * Green above the zero line, red below, split by clipping the SAME filled
 * area twice rather than by cutting the polygon at its crossings. The
 * geometry is identical either way and the clip version cannot get the
 * crossing points subtly wrong, which is the whole class of bug a
 * hand-rolled split invites.
 *
 * Purely decorative to a screen reader: the card states its legs in text
 * right beside this, so announcing the picture would only repeat them.
 */

import type { ReactElement } from "react";
import { shapeOf } from "@/lib/strategyShape";
import type { TemplateLeg } from "@/lib/strategyTemplates";

const W = 100;
const H = 34;

export function PayoffThumb({
  legs,
  id,
}: Readonly<{ legs: TemplateLeg[]; id: string }>): ReactElement {
  const shape = shapeOf(legs);
  const zeroY = shape.zero * H;
  const pts = shape.points.map((p) => `${(p.x * W).toFixed(2)},${(p.y * H).toFixed(2)}`);
  // Close the outline back along the zero line to get a fillable area.
  const area = `M ${pts.join(" L ")} L ${String(W)},${zeroY.toFixed(2)} L 0,${zeroY.toFixed(2)} Z`;
  const line = `M ${pts.join(" L ")}`;

  return (
    <svg
      viewBox={`0 0 ${String(W)} ${String(H)}`}
      className="h-8 w-full"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <clipPath id={`above-${id}`}>
          <rect x="0" y="0" width={W} height={zeroY} />
        </clipPath>
        <clipPath id={`below-${id}`}>
          <rect x="0" y={zeroY} width={W} height={H - zeroY} />
        </clipPath>
      </defs>
      {/* Same tokens and the same clip-twice construction as the full-size
          PayoffChart, so the two read as one system. The opacity is lifted
          from its 0.14 though: at 34px tall the big chart's wash is almost
          invisible, and here the FILL is the whole signal. */}
      <path d={area} fill="var(--fill-success)" opacity={0.3} clipPath={`url(#above-${id})`} />
      <path d={area} fill="var(--fill-danger)" opacity={0.3} clipPath={`url(#below-${id})`} />
      <line x1="0" y1={zeroY} x2={W} y2={zeroY} stroke="var(--line, #e5e7eb)" strokeWidth="0.5" />
      <path
        d={line}
        fill="none"
        stroke="var(--ink-secondary, #6b7280)"
        strokeWidth="1.25"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
